import React, { useEffect } from 'react';
import { Popover } from '@barges/react';
import { analyticsService } from '@common/analytics';
import { changeAppStateAPI } from '@common/api/api';
import { usePurchaseFlowRenderProps } from '@common/config/purchaseFlowRenderHelpers';
import SelectChatCredit from '../chat_overlay/select_chat_credit';
import NotInLiveMode from '../chat_overlay/not_in_live_mode';
import CantCreate from '../chat_overlay/cant_create';
import CreatedVoipChat from '../created_voip_chat';
import GeneralError from '../chat_overlay/general_error';
import Constants from '../../config/const';
import IntroComponent from '../intro';
import PurchaseFlowWrapper from '../purchase_flow/containers/PurchaseFlowWrapper';
import NoAnswer from '../chat_overlay/no_answer';
import classes from './classes.module.scss';
import { log, LOG_LEVELS } from '../../../common/config/app_logger';
import Spiner from '../../../common/helpers/spiner';
import { useZendeskMessaging } from '../zendesk_messaging_provider';

const { shared } = window;

function VoipChatOverlay({
  voipState,
  purchaseOnBack,
  purchaseSuccess,
  cancelChat,
  voipType,
  continuedSession,
  orderId
}) {
  const { renderIcon, renderText } = usePurchaseFlowRenderProps();
  const { setHidden } = useZendeskMessaging();
  let content = null;

  useEffect(() => {
    setHidden(true);
    return () => setHidden(false);
  }, [setHidden]);

  useEffect(() => {
    const originalBeforeUnload = window.onbeforeunload;
    window.onbeforeunload = () => {
      log(LOG_LEVELS.INFO, 'VOIP CHAT', 'Page refreshed');
      return 'Test message';
    };

    const handlePageHide = () => {
      changeAppStateAPI(orderId, 'closed_window');
    };
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.onbeforeunload = originalBeforeUnload;
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [orderId]);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  switch (voipState) {
    case Constants.voipChatStates.showSpin:
      log(LOG_LEVELS.DEBUG, 'VOIP CHAT', 'State changed', { state: 'showSpin' });
      content = (
        <div className={ classes.callingScreen }>
          <Spiner loaderColor={ shared.appColor } />
        </div>
      );
      break;
    case Constants.voipChatStates.paymentRequired:
      log(LOG_LEVELS.DEBUG, 'VOIP CHAT', 'State changed', { state: 'paymentRequired' });
      content = (
        <PurchaseFlowWrapper
          mode="additionalFunds"
          onComplete={ purchaseSuccess }
          onCancel={ purchaseOnBack }
          onAnalyticsEvent={ (...args) => analyticsService.trackEvent(...args) }
          renderIcon={ renderIcon }
          renderText={ renderText }
          inModal
          popoverContentClassName={ classes.purchaseDetailsPopupContent }
          chatType={ voipType }
          hidePaymentDetails={ purchaseOnBack }
          backArrowAction={ purchaseOnBack }
        />
      );
      break;
    case Constants.voipChatStates.initial:
      log(LOG_LEVELS.DEBUG, 'VOIP CHAT', 'State changed', { state: 'appInitial' });
      content = (
        <Popover isOpen className="appInitialPopupContainer">
          <SelectChatCredit
            chatType={ voipType }
            isContinue={ continuedSession > 0 }
            selectorOpenedTime={ new Date() }
          />
        </Popover>
      );
      break;
    case Constants.voipChatStates.introduce:
      log(LOG_LEVELS.DEBUG, 'VOIP CHAT', 'State changed', { state: 'introduce' });
      content = (
        <div className={ classes.callingScreen }>
          <IntroComponent chatType={ voipType } closeButtonAction={ cancelChat } />
        </div>
      );
      break;
    case Constants.voipChatStates.noAnswer:
      log(LOG_LEVELS.DEBUG, 'VOIP CHAT', 'State changed', { state: 'noAnswer' });
      content = (
        <div className={ classes.callingScreen }>
          <NoAnswer chatType={ voipType } />
        </div>
      );
      break;
    case Constants.voipChatStates.notInLiveMode:
      log(LOG_LEVELS.DEBUG, 'VOIP CHAT', 'State changed', { state: 'appNotInLiveMode' });
      content = (
        <div className={ classes.callingScreen }>
          <NotInLiveMode chatType={ voipType } />
        </div>
      );
      break;
    case Constants.voipChatStates.appCantCreate:
      log(LOG_LEVELS.DEBUG, 'VOIP CHAT', 'State changed', { state: 'appCantCreate' });
      content = (
        <div className={ classes.callingScreen }>
          <CantCreate chatType={ voipType } />
        </div>
      );
      break;
    case Constants.voipChatStates.unknownError:
      log(LOG_LEVELS.ERROR, 'VOIP CHAT', 'Error state', { error: 'generalError' });
      content = (
        <div className={ classes.callingScreen }>
          <GeneralError chatType={ voipType } />
        </div>
      );
      break;
    default:
      // once we have an id, it means that CreatedChat will take care of the rendering
      log(LOG_LEVELS.INFO, 'VOIP CHAT', 'Chat created');
      content = <CreatedVoipChat />;
      break;
  }

  return <div className={ classes.layout }>{content}</div>;
}

export default VoipChatOverlay;
